import React, { useState, useRef, useEffect } from "react";
import { formatMoney } from "../../features/utils/formatMoney";
import odiopayRepayments from "../../controllers/Flight/odiopay";
import moment from "moment";
import axios from "axios";

function PaymentMethodOdiopaySection({ booking, setData }) {
  const grandTotal = booking?.booking?.flightBooking?.at(0)?.grandTotal || 0;
  const minAmount = Math.ceil(grandTotal / 2);
  const [dropdown, setDropdown] = useState(false);
  const [amount, setAmount] = useState();
  const [payload, setPayload] = useState({
    min: "",
    plan: 3,
  });
  const [loading, setLoading] = useState(false);
  const [repayments, setRepayments] = useState([]);
  const [serviceFee, setServiceFee] = useState(0);
  const cancelRepayementToken = useRef(null);

  const installmentOptions = [1, 2, 3];

  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdown(false);
      }
    }
    if (dropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdown]);

  useEffect(() => {
    let formatted = new Intl.NumberFormat().format(Number(minAmount));
    setPayload({ ...payload, min: formatted });
    setAmount(formatted);
  }, []);

  const loadData = async () => {
    setLoading(true);
    let raw = Number(payload.min.replace(/\D/g, ""));
    const min = ((raw / grandTotal) * 100).toFixed(2);
    if (raw < minAmount) return;
    if (raw > grandTotal) return;
    const obj = {
      flightBookingId: booking?.booking?.flightBooking?.at(0)?._id,
      min: parseInt(min),
      plan: payload.plan,
      viewOnly: true,
    };
    setData({ ...obj, viewOnly: false });
    let cancellationMessage = "Operation canceled due to new request.";

    if (cancelRepayementToken.current) {
      cancelRepayementToken.current.cancel(cancellationMessage);
    }

    cancelRepayementToken.current = axios.CancelToken.source();
    await odiopayRepayments(obj, cancelRepayementToken.current.token).then(
      (res) => {
        if (!res?.cancelled) setLoading(false);
        if (res?.data?.repayments) {
          setServiceFee(res?.data?.serviceFee);
          setRepayments([
            { amount: res?.data?.firstPayment, paymentDate: "today" },
            ...res?.data?.repayments,
          ]);
        }
      }
    );
  };

  useEffect(() => {
    loadData();
  }, [payload]);

  const handleChange = (e) => {
    // Only keep digits
    let raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setAmount("");
      setPayload({ ...payload, min: "" });
      return;
    }
    // Format number with commas
    let formatted = new Intl.NumberFormat().format(Number(raw));
    setAmount(formatted);
    setPayload({ ...payload, min: formatted });
  };

  const handleBlur = () => {
    let raw = Number(amount.replace(/\D/g, ""));
    if (raw < minAmount) {
      let formatted = new Intl.NumberFormat().format(Number(minAmount));
      setPayload({ ...payload, min: formatted });
    } else if (raw > grandTotal) {
      let formatted = new Intl.NumberFormat().format(Number(grandTotal));
      setPayload({ ...payload, min: formatted });
    }
  };

  return (
    <div className="w-full">
      <div className="w-full flex justify-between border-b border-gray-300 pb-3 gap-10">
        {/* Installment Dropdown */}
        <div
          className="bg-[#F5F5F5] border border-black/30 rounded-md p-2 flex flex-col flex-1 relative cursor-pointer"
          ref={dropdownRef}
          onClick={() => setDropdown((prev) => !prev)}
        >
          <small>Installments</small>
          <hr className="w-full border-white my-2" />
          <div className="w-full flex justify-between items-center">
            <span>
              {payload?.plan} time{payload?.plan === 1 ? "" : "s"}
            </span>
            <img
              src="/IconChevronDown.svg"
              alt=""
              className={`w-3 object-contain transition-all ${
                dropdown ? "rotate-180" : ""
              }`}
            />
          </div>
          <div className="h-6"></div>
          {dropdown && (
            <div className="absolute left-0 top-full mt-2 z-20 bg-white shadow-lg border rounded w-full">
              {installmentOptions.map((option) => (
                <div
                  key={payload.plan}
                  className={`px-4 py-2 hover:bg-blue-100 cursor-pointer ${
                    option === payload?.plan ? "bg-blue-50 font-semibold" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPayload({ ...payload, plan: option });
                    setDropdown(false);
                  }}
                >
                  {option} time{option === 1 ? "" : "s"}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Minimum Payable */}
        <div className="bg-[#F5F5F5] border border-black/30 rounded-md p-2 flex  flex-col flex-1">
          <small>How much do you want to pay now?</small>
          <hr className="w-full border-white my-2" />
          <div className="flex gap-2 items-center w-full">
            <span className="font-semibold">₦</span>
            <input
              type="text"
              className="w-full flex text-base px-2 py rounded-md"
              value={payload.min}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
          <div className="h-6">
            {" "}
            <small className="text-primary1">
              Minimum payable: {formatMoney(minAmount)}{" "}
            </small>{" "}
          </div>
        </div>
      </div>
      <div className="w-full mt-4">
        <span className="font-semibold my-2 ">Your Payment Schedule</span>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-4 min-h-[200px]">
          <div className="primaryLoader"></div>
        </div>
      ) : (
        <div className="w-full mt-2 pb-4 border-b border-gray-300">
          {repayments?.map((item, itemIndex) => (
            <div key={itemIndex} className="flex flex-col w-full group">
              <div className="flex w-full items-center justify-between">
                <div className="gap-2 flex items-center">
                  <div className="bg-[#6D7C94] w-4 h-4 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  </div>
                  <span className="text-[#6D7C94]">
                    {item?.paymentDate === "today"
                      ? "Today"
                      : moment(item?.paymentDate).format("DD MMM YYYY")}
                  </span>
                </div>
                <span className="">{formatMoney(item.amount)}</span>
              </div>
              <div className="w-1 h-10 bg-[#6D7C94] -my-2 ml-[6px] group-last:hidden flex"></div>
            </div>
          ))}
        </div>
      )}
      {serviceFee && !loading && (
        <div className="pt-3 pb-1 w-full flex justify-between items-center">
          <div className="flex items-center ">
            <span>Service charge: </span>{" "}
          </div>
          <div className="flex items-center ">
            <span>{formatMoney(serviceFee)}</span>
          </div>
        </div>
      )}
      <div className="pt-1 pb-1 w-full flex justify-between items-center">
        <div className="flex items-center font-semibold">
          <span>Total Payable: </span>{" "}
        </div>
        <div className="flex items-center font-semibold">
          {loading ? (
            <div className="primaryLoader"></div>
          ) : (
            <span>
              {formatMoney(
                repayments.reduce((acc, current) => {
                  return acc + current?.amount;
                }, 0)
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default PaymentMethodOdiopaySection;
